// src/pages/Checkout.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getStorefrontProducts, getDiscountCode } from '../db';
import { ProductThumb } from '../components/ProductPhoto';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  CssBaseline,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTheme, alpha } from '@mui/material/styles';
import { CreditCard, Lock, MapPin, ShieldCheck, Tag, User } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { readStoredCart, reconcileCart, writeStoredCart } from '../products';
import { itemNameOf } from '../catalogue';
import { useSeo } from '../seo';
import { UNIT_PRICE, describeRule, discountRule, priceLine } from '../pricing';

/** Rands, the way every figure on this page is written. */
const rand = (value) => `R${Number(value || 0).toFixed(2)}`;

/**
 * Whether a phone number has enough digits to be dialled. Spaces, dashes,
 * dots and brackets are how people write numbers, so they are allowed; the
 * same rule is applied again server-side in functions/index.js.
 */
const isPhoneNumber = (value) => /^\+?\d{9,15}$/.test(String(value || '').replace(/[\s\-().]/g, ''));

// Use the Cloud Run URLs, not the *.cloudfunctions.net aliases. The alias
// validates any Authorization: Bearer header as a Google IAM token and rejects
// our Firebase ID token with a 401 that carries no CORS headers, so the browser
// reports it as a CORS failure. The run.app URL passes the header through to us.
const CHECKOUT_ENDPOINTS = {
  payfast: 'https://createpayfastcheckout-bb4d2hrpta-uc.a.run.app',
  yoco: 'https://createyococheckout-bb4d2hrpta-uc.a.run.app',
  options: 'https://paymentoptions-bb4d2hrpta-uc.a.run.app',
};

// PayFast is the preferred provider and is listed (and selected) first.
const PAYMENT_PROVIDERS = [
  {
    id: 'payfast',
    label: 'PayFast',
    blurb: 'Card, Instant EFT, SnapScan, Zapper and more',
    recommended: true,
  },
  {
    id: 'yoco',
    label: 'Yoco',
    blurb: 'Pay by debit or credit card',
    recommended: false,
  },
];

/**
 * PayFast has no server-side checkout-creation call: the merchant signs a set of
 * fields and the customer's browser POSTs them. Build that form and submit it.
 */
const submitPayfastForm = (url, fields) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.style.display = 'none';
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const Checkout = () => {
  useSeo({ title: 'Checkout | Crestancia', path: '/checkout', noindex: true });

  const [processing, setProcessing] = useState(false);
  // Which providers are offered is decided server-side. Until that answer
  // arrives, assume only the unrestricted one.
  const [availableProviders, setAvailableProviders] = useState(null);
  const [payfastRestricted, setPayfastRestricted] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState('yoco');
  // What is in the basket, by product id, read from the cart on mount and
  // trimmed to what is in stock once the catalogue has been read.
  const [quantities, setQuantities] = useState(() => readStoredCart().items);
  // The catalogue, in the shop's order. Empty until read.
  const [products, setProducts] = useState([]);
  // What can be sold of each product; absent (unknown) until read.
  const [stock, setStock] = useState({});
  const [total, setTotal] = useState(0);

  const [discountCode, setDiscountCode] = useState('');
  // The four maps a code carries, kept together because they are read together:
  // a percentage or a fixed price per unit for each product, and how many units
  // the rule reaches before the ordinary price applies again.
  const [codeDiscount, setCodeDiscount] = useState({});
  // A flat charge carried by the code, added once to the order. The server
  // reads it back off the code, so this is only what the customer is shown.
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [codeValid, setCodeValid] = useState(false);
  
  // Customer details
  const [customerEmail, setCustomerEmail] = useState('guest@example.com');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  // Unticked until the customer ticks it: POPIA does not count a box ticked
  // for them as consent.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerPostalCode, setCustomerPostalCode] = useState('');

  // Auto-populate email from Firebase Auth, and ask the server which payment
  // providers this user may use. PayFast is admin-only while it runs against
  // the sandbox, and that rule lives on the server so the UI cannot drift from
  // what the checkout endpoint will actually accept.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.email) {
        setCustomerEmail(user.email);
      }
      try {
        const token = user ? await user.getIdToken() : null;
        const { data } = await axios.get(CHECKOUT_ENDPOINTS.options, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 15000,
        });
        const allowed =
          Array.isArray(data?.providers) && data.providers.length
            ? data.providers
            : ['yoco'];
        setAvailableProviders(allowed);
        setPayfastRestricted(Boolean(data?.payfastRestricted));
        // Prefer the first provider the server offers — PayFast when present.
        setPaymentProvider((current) => (allowed.includes(current) ? current : allowed[0]));
      } catch (err) {
        setAvailableProviders(['yoco']);
        setPaymentProvider('yoco');
      }
    });
    return unsubscribe;
  }, []);

  // Fetch stock and prices from database
  useEffect(() => {
    fetchStockAndPrices();
  }, []);

  const productsById = useMemo(() => {
    const map = {};
    products.forEach((product) => {
      map[product.id] = product;
    });
    return map;
  }, [products]);

  /** One of a product, as the order, the email and the invoice will name it. */
  const nameOf = (id) => itemNameOf(productsById[id]) || 'This item';

  // Unit prices, keyed the way the pricing rules are.
  const unitPrices = useMemo(() => {
    const map = {};
    products.forEach((product) => {
      map[product.id] = Number(product.price) || 0;
    });
    return map;
  }, [products]);

  // Every product the page can show — what is in the basket and everything
  // else on sale — in the shop's order.
  const productIds = useMemo(
    () =>
      products
        .filter((product) => (quantities[product.id] || 0) > 0 || product.isForSale !== false)
        .map((product) => product.id),
    [products, quantities]
  );

  /**
   * Every line priced against the code, if one is applied.
   *
   * Display only — the server prices the order again from the code and the
   * stock records before charging anything, so this can never decide the
   * amount. It is here so the customer sees the same figure they will pay,
   * including the point at which a unit limit stops discounting.
   */
  const pricedLines = useMemo(() => {
    const source = codeValid ? codeDiscount : {};
    const lines = {};
    productIds.forEach((id) => {
      lines[id] = priceLine(quantities[id] || 0, unitPrices[id], discountRule(source, id));
    });
    return lines;
  }, [productIds, quantities, unitPrices, codeDiscount, codeValid]);

  const subtotal = useMemo(
    () => productIds.reduce((sum, id) => sum + pricedLines[id].gross, 0),
    [productIds, pricedLines]
  );
  const discountAmount = useMemo(
    () => productIds.reduce((sum, id) => sum + pricedLines[id].discount, 0),
    [productIds, pricedLines]
  );

  // Recalculate total whenever quantities, prices or discount change
  useEffect(() => {
    const delivery = codeValid ? Number(deliveryFee) || 0 : 0;
    setTotal(subtotal - discountAmount + delivery);
  }, [subtotal, discountAmount, deliveryFee, codeValid]);

  const fetchStockAndPrices = async () => {
    try {
      const data = await getStorefrontProducts();
      // Read the current cart from localStorage to avoid clearing it. Off sale
      // or out of stock both read as nothing to sell, so the basket is trimmed
      // to match rather than left to fail at payment.
      const live = reconcileCart(readStoredCart(), data);
      const names = {};
      data.forEach((product) => {
        names[product.id] = itemNameOf(product);
      });
      if (live.trimmed.length > 0) {
        toast.warning(
          `Not enough stock — your order was reduced for: ${live.trimmed
            .map((id) => names[id])
            .join(', ')}`
        );
      }
      setProducts(data);
      setStock(live.stock);
      setQuantities(live.items);
      writeStoredCart(live.items, live.prices, live.names);
    } catch (err) {
      toast.error("Error fetching stock and prices: " + err.message);
    }
  };

  // The products in the basket, in the shop's order.
  const basketIds = productIds.filter((id) => (quantities[id] || 0) > 0);
  const needsCode = (id) => productsById[id]?.needsCode === true;
  const acceptsCode = (id, code) => (productsById[id]?.acceptableCodes || []).includes(code);

  const increment = (id) => {
    const qty = quantities[id] || 0;
    const limit = stock[id];
    if (limit !== null && limit !== undefined && qty < limit) {
      setQuantities((current) => ({ ...current, [id]: qty + 1 }));
    } else {
      toast.warning(`Reached maximum available stock for ${nameOf(id)}.`);
    }
  };

  const decrement = (id) => {
    const qty = quantities[id] || 0;
    if (qty > 0) {
      setQuantities((current) => ({ ...current, [id]: qty - 1 }));
    }
  };

  const verifyDiscountCode = async () => {
    setCodeValid(false);
    setCodeDiscount({});
    setDeliveryFee(0);
    if (!discountCode) return;
    try {
      const doc = await getDiscountCode(discountCode);
      const email = customerEmail;
      if (
        doc &&
        (doc.uses || 0) < (doc.maxUses || 1) &&
        (doc.allowMultipleUsesPerUser || !doc.usedByEmails?.includes(email))
      ) {
        const invalidItems = basketIds
          .filter((id) => needsCode(id) && !acceptsCode(id, discountCode))
          .map(nameOf);
        if (invalidItems.length > 0) {
          toast.error(`Code not valid for: ${invalidItems.join(', ')}`);
          return;
        }
        const fee = Number(doc.deliveryFee) || 0;
        setCodeDiscount({
          discountPercents: doc.discountPercents || {},
          discountTypes: doc.discountTypes || {},
          discountUnitPrices: doc.discountUnitPrices || {},
          discountMaxUnits: doc.discountMaxUnits || {},
        });
        setDeliveryFee(fee > 0 ? fee : 0);
        setCodeValid(true);
        toast.success(
          fee > 0
            ? `Delivery code applied — R${fee.toFixed(2)} delivery added`
            : 'Delivery code applied'
        );

        // A unit limit is the one thing about a code that surprises people, so
        // say it as soon as the basket is already past it rather than leaving
        // the customer to work out why the total is higher than they expected.
        const past = basketIds.filter((id) => {
          const rule = discountRule(doc, id);
          return rule.maxUnits > 0 && (quantities[id] || 0) > rule.maxUnits;
        });
        if (past.length > 0) {
          toast.info(
            past
              .map((id) => {
                const rule = discountRule(doc, id);
                return `${nameOf(id)}: first ${rule.maxUnits} discounted, the rest at the usual price`;
              })
              .join(' · ')
          );
        }
      } else {
        toast.error('Invalid or used code');
      }
    } catch (err) {
      toast.error('Error checking code: ' + err.message);
    }
  };

  const handlePurchase = async () => {
    if (processing) return;
    setProcessing(true);

    try {
      // Check stock availability — only for what is actually being bought. A
      // product left at zero must never block the order, whatever its stock.
      const short = basketIds.filter(
        (id) => stock[id] !== null && stock[id] !== undefined && quantities[id] > stock[id]
      );
      if (short.length > 0) {
        throw new Error(`Not enough stock available for: ${short.map(nameOf).join(', ')}`);
      }
      // Validate customer details
      if (!customerName || !customerEmail || !customerPhone || !customerAddress || !customerCity || !customerPostalCode) {
        throw new Error("Please fill in all the required customer details.");
      }
      if (!isPhoneNumber(customerPhone)) {
        throw new Error("Please enter a phone number we can reach you on.");
      }
      // Check code requirements
      const itemsNeedingCode = basketIds
        .filter((id) => needsCode(id) && (!codeValid || !acceptsCode(id, discountCode)))
        .map(nameOf);
      if (itemsNeedingCode.length > 0) {
        throw new Error(`The following items require a valid code: ${itemsNeedingCode.join(', ')}`);
      }
      // Send only what is being bought. Prices, discounts and the final amount
      // are resolved server-side — never trust the browser with the total.
      const items = {};
      basketIds.forEach((id) => {
        items[id] = quantities[id];
      });
      const payload = {
        items,
        customer: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone.trim(),
          address: customerAddress,
          city: customerCity,
          postalCode: customerPostalCode,
          marketingOptIn,
        },
        discountCode: codeValid ? discountCode : '',
        origin: window.location.origin,
      };

      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error("Please sign in again before paying.");
      }

      const provider = PAYMENT_PROVIDERS.find((p) => p.id === paymentProvider);
      const response = await axios.post(
        CHECKOUT_ENDPOINTS[paymentProvider],
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          withCredentials: false,
          timeout: 20000
        }
      );

      if (paymentProvider === 'payfast') {
        if (!response.data?.url || !response.data?.fields) {
          throw new Error("No checkout details returned by server");
        }
        toast.info(`Redirecting to ${provider.label}...`);
        submitPayfastForm(response.data.url, response.data.fields);
        // The browser is navigating away; leave the button disabled.
        return;
      }

      if (response.data?.redirectUrl) {
        toast.info(`Redirecting to ${provider.label}...`);
        window.location.href = response.data.redirectUrl;
      } else {
        throw new Error("No checkout URL returned by server");
      }
    } catch (err) {
      let errorMessage = "Checkout error: ";
      if (err.response?.status === 405) {
        errorMessage += "Invalid request method";
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += "Request timed out";
      } else if (err.message.includes('Network Error')) {
        errorMessage += "CORS error - Please ensure the server allows requests from this origin";
      } else if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else {
        errorMessage += err.message;
      }
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const theme = useTheme();

  const providersLoading = availableProviders === null;
  const visibleProviders = PAYMENT_PROVIDERS.filter((p) =>
    (availableProviders || ['yoco']).includes(p.id)
  );
  const selectedProvider =
    visibleProviders.find((p) => p.id === paymentProvider) || visibleProviders[0];

  /** One line per product the applied code actually discounts. */
  const codeRuleSummaries = useMemo(() => {
    if (!codeValid) return [];
    return products
      .map((product) => ({ id: product.id, rule: discountRule(codeDiscount, product.id) }))
      .map(({ id, rule }) => ({ id, text: describeRule(rule, rand) }))
      .filter((entry) => entry.text)
      .map((entry) => ({ ...entry, text: `${itemNameOf(productsById[entry.id])} — ${entry.text}` }));
  }, [codeDiscount, codeValid, products, productsById]);

  /** Every product the page shows, with its stepper wired up. */
  const lineItems = productIds.map((id) => ({
    id,
    name: nameOf(id),
    qty: quantities[id] || 0,
    price: unitPrices[id] || 0,
    increment: () => increment(id),
    decrement: () => decrement(id),
  }));

  const inOrder = lineItems.filter((line) => line.qty > 0);
  // Everything else on sale stays reachable, so a product can still be added
  // here rather than only from the shop.
  const notInOrder = lineItems.filter((line) => line.qty === 0);

  // Olive already lightens with the mode, so the accent is simply the brand.
  const accent = theme.palette.primary.main;
  const cardSx = {
    p: { xs: 2.5, md: 3 },
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
  };

  const SectionTitle = ({ icon, children }) => (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', color: accent }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{children}</Typography>
    </Stack>
  );

  const Stepper = ({ line }) => (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ borderRadius: 999, border: '1px solid', borderColor: 'divider', px: 0.5 }}
    >
      <IconButton
        size="small"
        onClick={line.decrement}
        aria-label={`Remove one ${line.name}`}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 26, textAlign: 'center', fontWeight: 700 }}>{line.qty}</Typography>
      <IconButton
        size="small"
        onClick={line.increment}
        aria-label={`Add one ${line.name}`}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const providerName = selectedProvider ? selectedProvider.label : 'the provider';

  const orderSummary = (
    <Paper elevation={0} sx={{ ...cardSx, position: { md: 'sticky' }, top: { md: 24 } }}>
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        Order summary
      </Typography>

      <Stack spacing={1.25} sx={{ mt: 2.5 }}>
        {inOrder.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            Nothing added yet.
          </Typography>
        ) : (
          inOrder.map((line) => {
            const priced = pricedLines[line.id];
            return (
              <Box key={line.id}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                    {line.name} &times; {line.qty}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                    {rand(priced.gross)}
                  </Typography>
                </Stack>
                {priced.discount > 0 && (
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: 'success.main', fontSize: '0.78rem' }}>
                      {priced.rule.type === UNIT_PRICE
                        ? `${rand(priced.rule.unitPrice)} each`
                        : `${priced.rule.percent}% off`}
                      {priced.capReached
                        ? ` on ${priced.discountedUnits} of ${priced.qty}`
                        : ''}
                    </Typography>
                    <Typography sx={{ color: 'success.main', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      -{rand(priced.discount)}
                    </Typography>
                  </Stack>
                )}
              </Box>
            );
          })
        )}
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: 'text.secondary' }}>Subtotal</Typography>
          <Typography>{rand(subtotal)}</Typography>
        </Stack>
        {codeValid && discountAmount > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: 'success.main' }}>Discount</Typography>
            <Typography sx={{ color: 'success.main' }}>-{rand(discountAmount)}</Typography>
          </Stack>
        )}
        {codeValid && deliveryFee > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: 'text.secondary' }}>Delivery</Typography>
            <Typography>+{rand(deliveryFee)}</Typography>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={{ fontWeight: 700 }}>Total</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1.6rem' }}>{rand(total)}</Typography>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={handlePurchase}
        disabled={
          processing ||
          providersLoading ||
          basketIds.length === 0
        }
        sx={{
          mt: 3,
          py: 1.6,
          borderRadius: 999,
          fontWeight: 700,
          fontSize: '1rem',
          textTransform: 'none',
        }}
      >
        {processing
          ? 'Processing Payment...'
          : providersLoading
          ? 'Loading payment options...'
          : `Pay with ${selectedProvider ? selectedProvider.label : 'card'}`}
      </Button>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={0.75}
        sx={{ mt: 2, color: 'text.secondary' }}
      >
        <Lock size={13} />
        <Typography sx={{ fontSize: '0.78rem' }}>
          Payment is taken on the secure {providerName} page
        </Typography>
      </Stack>
    </Paper>
  );

  return (
    <>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        {/* A checkout-only bar. The full site header carries the cart dropdown,
            which edits the same localStorage this page read on mount — the two
            would drift apart mid-order. */}
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 1.75 }}
            >
              <Box component={Link} to="/home" sx={{ textDecoration: 'none', display: 'flex' }}>
                <BrandMark size="sm" />
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ color: 'text.secondary' }}
              >
                <ShieldCheck size={16} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                  Secure checkout
                </Typography>
              </Stack>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Checkout
            </Typography>
            <Button component={Link} to="/home" sx={{ textTransform: 'none', fontWeight: 600 }}>
              &larr; Back to shop
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              alignItems: 'start',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.8fr) minmax(0, 1fr)' },
            }}
          >
            <Stack spacing={3}>
              <Paper elevation={0} sx={cardSx}>
                <SectionTitle icon={<Tag size={19} />}>Your order</SectionTitle>

                <Stack spacing={2}>
                  {inOrder.map((line) => (
                    <Box
                      key={line.id}
                      sx={{
                        display: 'grid',
                        gap: 2,
                        alignItems: 'center',
                        gridTemplateColumns: { xs: '64px 1fr', sm: '72px 1fr auto' },
                      }}
                    >
                      <ProductThumb
                        productId={line.id}
                        alt={line.name}
                        size={72}
                        sx={{
                          width: { xs: 64, sm: 72 },
                          height: { xs: 64, sm: 72 },
                          borderRadius: 2,
                          gridRow: { xs: 'span 2', sm: 'auto' },
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700 }}>{line.name}</Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
                          {rand(line.price)} each
                        </Typography>
                        {needsCode(line.id) && (
                          <Chip
                            size="small"
                            label="Requires code"
                            color="warning"
                            variant="outlined"
                            sx={{ mt: 0.75, height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      <Stack
                        direction={{ xs: 'row', sm: 'column' }}
                        alignItems={{ xs: 'center', sm: 'flex-end' }}
                        justifyContent="space-between"
                        spacing={{ xs: 2, sm: 1 }}
                        sx={{ gridColumn: { xs: '2', sm: 'auto' } }}
                      >
                        <Stepper line={line} />
                        <Typography sx={{ fontWeight: 700 }}>
                          {rand(line.qty * line.price)}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}

                  {inOrder.length === 0 && (
                    <Typography sx={{ color: 'text.secondary' }}>
                      Your order is empty. Add something below, or head back to the shop.
                    </Typography>
                  )}
                </Stack>

                {notInOrder.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        mb: 2,
                      }}
                    >
                      Add something else
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      }}
                    >
                      {notInOrder.map((line) => (
                        <Stack
                          key={line.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1.5}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }} noWrap>
                              {line.name}
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {rand(line.price)} each
                            </Typography>
                          </Box>
                          <Stepper line={line} />
                        </Stack>
                      ))}
                    </Box>
                  </>
                )}
              </Paper>

              <Paper elevation={0} sx={cardSx}>
                <SectionTitle icon={<User size={19} />}>Your details</SectionTitle>
                <Box component="form" noValidate autoComplete="off">
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    type="tel"
                    inputProps={{ autoComplete: 'tel', inputMode: 'tel' }}
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    helperText="So we can contact you about this order and its delivery."
                    required
                  />
                  {/* POPIA wants direct marketing agreed to, not assumed, and
                      the choice offered when the details are taken. */}
                  <FormControlLabel
                    sx={{ mt: 1.5, alignItems: 'flex-start', mr: 0 }}
                    control={
                      <Checkbox
                        checked={marketingOptIn}
                        onChange={(e) => setMarketingOptIn(e.target.checked)}
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.5 }}>
                        Send me news and specials from Crestancia by email, SMS or WhatsApp. I can
                        unsubscribe at any time.
                      </Typography>
                    }
                  />
                  {/* POPIA asks that people are told, when a detail is taken,
                      what it is for and who else gets it. */}
                  <Typography sx={{ mt: 1.5, fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.6 }}>
                    We use these details to process, deliver and confirm your order, and for
                    marketing only if you tick the box above. Your phone number and address are not
                    shared with the payment provider. See our{' '}
                    <Box
                      component={Link}
                      to="/privacy"
                      target="_blank"
                      sx={{ color: accent, fontWeight: 600 }}
                    >
                      privacy notice
                    </Box>
                    .
                  </Typography>
                </Box>
              </Paper>

              <Paper elevation={0} sx={cardSx}>
                <SectionTitle icon={<MapPin size={19} />}>Delivery address</SectionTitle>
                <Box component="form" noValidate autoComplete="off">
                  <TextField
                    fullWidth
                    label="Address"
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={customerCity}
                        onChange={(e) => setCustomerCity(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={customerPostalCode}
                        onChange={(e) => setCustomerPostalCode(e.target.value)}
                        required
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.92rem' }}>
                    Have a delivery code?
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                    <TextField
                      fullWidth
                      label="Delivery Code"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    />
                    <Button
                      onClick={verifyDiscountCode}
                      variant="outlined"
                      sx={{
                        minWidth: { sm: 120 },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Apply
                    </Button>
                  </Stack>
                  {codeValid && (
                    <Box sx={{ mt: 1.5 }}>
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`Code ${discountCode} applied`}
                      />
                      {/* What the code covers, spelled out — a unit limit only
                          shows up in the total once the basket passes it, which
                          is far too late for the customer to learn about it. */}
                      {codeRuleSummaries.length > 0 && (
                        <Stack spacing={0.25} sx={{ mt: 1 }}>
                          {codeRuleSummaries.map((summary) => (
                            <Typography
                              key={summary.id}
                              sx={{ fontSize: '0.78rem', color: 'text.secondary' }}
                            >
                              {summary.text}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>

              <Paper elevation={0} sx={cardSx}>
                <SectionTitle icon={<CreditCard size={19} />}>Payment method</SectionTitle>
                <Box
                  role="radiogroup"
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  {visibleProviders.map((provider) => {
                    const selected = paymentProvider === provider.id;
                    return (
                      <Box
                        key={provider.id}
                        onClick={() => setPaymentProvider(provider.id)}
                        role="radio"
                        aria-checked={selected}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setPaymentProvider(provider.id);
                          }
                        }}
                        sx={{
                          p: 2,
                          height: '100%',
                          cursor: 'pointer',
                          borderRadius: 2.5,
                          border: '2px solid',
                          borderColor: selected ? accent : 'divider',
                          backgroundColor: selected
                            ? alpha(theme.palette.primary.main, 0.07)
                            : 'transparent',
                          transition: 'border-color .2s ease, background-color .2s ease',
                          '&:hover': { borderColor: selected ? accent : 'text.disabled' },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 700 }}>{provider.label}</Typography>
                          {provider.recommended && (
                            <Chip
                              size="small"
                              label="RECOMMENDED"
                              sx={{
                                height: 20,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                color: 'primary.contrastText',
                                backgroundColor: 'primary.main',
                              }}
                            />
                          )}
                        </Stack>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.86rem' }}>
                          {provider.blurb}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {paymentProvider === 'payfast' && payfastRestricted && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'warning.main',
                      backgroundColor: alpha(theme.palette.warning.main, 0.12),
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      PayFast sandbox — admin testing only
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No real money will be taken, but the order will be recorded and stock will
                      be reduced as if it were a live sale.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Stack>

            {orderSummary}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Checkout;
